export const SW_UPDATE_PENDING_STORAGE_KEY = 'guarnic:sw-update-pending';

/** Default wait for SKIP_WAITING to activate before considering a missed Workbox reload. */
export const SW_UPDATE_FALLBACK_RELOAD_MS = 1500;

type ApplyServiceWorkerUpdateOptions = {
  updateSW: (reloadPage?: boolean) => Promise<void> | void;
  reload?: () => void;
  /**
   * Fallback reload if Workbox's `controlling` reload never runs after SKIP_WAITING
   * (seen when a waiting worker activates but the document stays on the
   * previous JS bundle — modal gone, version stuck).
   */
  fallbackReloadMs?: number;
  serviceWorker?: ServiceWorkerContainer | undefined;
  /** Used to detect whether SKIP_WAITING actually cleared the waiting worker. */
  registration?: ServiceWorkerRegistration | null;
};

/** Coalesces concurrent apply calls for the whole activate → reload lifecycle. */
let applyInFlight: Promise<void> | null = null;
/** Shared across callers so concurrent apply paths only reload once. */
let documentReloadScheduled = false;

/**
 * Activates a waiting service worker and guarantees a document reload.
 *
 * Happy path: `virtual:pwa-register` posts SKIP_WAITING and Workbox reloads on
 * `controlling` (when `isUpdate`). We intentionally do **not** also reload on
 * native `controllerchange` — that raced Workbox and caused double navigations.
 *
 * Escape hatch: if waiting clears but no reload happened within
 * {@link SW_UPDATE_FALLBACK_RELOAD_MS}, reload once (missed `controlling` /
 * `isUpdate === false`). If waiting is still present or registration is unknown,
 * mark pending and stop — avoid soft reload loops.
 */
export const applyServiceWorkerUpdate = (
  options: ApplyServiceWorkerUpdateOptions,
): Promise<void> => {
  if (documentReloadScheduled) {
    return Promise.resolve();
  }

  if (applyInFlight) {
    return applyInFlight;
  }

  applyInFlight = applyServiceWorkerUpdateInternal(options).finally(() => {
    applyInFlight = null;
  });

  return applyInFlight;
};

const resolveRegistration = async (
  registration: ServiceWorkerRegistration | null | undefined,
  serviceWorker: ServiceWorkerContainer | undefined,
): Promise<ServiceWorkerRegistration | undefined> => {
  if (registration) {
    return registration;
  }
  if (serviceWorker && typeof serviceWorker.getRegistration === 'function') {
    return (await serviceWorker.getRegistration()) ?? undefined;
  }
  return undefined;
};

const applyServiceWorkerUpdateInternal = ({
  updateSW,
  reload = () => window.location.reload(),
  fallbackReloadMs = SW_UPDATE_FALLBACK_RELOAD_MS,
  serviceWorker = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
  registration,
}: ApplyServiceWorkerUpdateOptions): Promise<void> =>
  new Promise(resolve => {
    let fallbackTimer: number | undefined;
    let settled = false;

    const settle = () => {
      if (settled) {
        return;
      }
      settled = true;
      if (fallbackTimer !== undefined) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }
      resolve();
    };

    const reloadOnce = () => {
      if (documentReloadScheduled) {
        settle();
        return;
      }
      documentReloadScheduled = true;
      clearServiceWorkerUpdatePending();
      reload();
      settle();
    };

    void (async () => {
      try {
        await updateSW(true);
      } catch {
        // Still evaluate the fallback path so a failed postMessage cannot leave
        // the user on a stale document after they asked to update.
      }

      if (documentReloadScheduled) {
        settle();
        return;
      }

      if (fallbackReloadMs <= 0) {
        settle();
        return;
      }

      fallbackTimer = window.setTimeout(() => {
        void (async () => {
          if (documentReloadScheduled) {
            settle();
            return;
          }

          const activeRegistration = await resolveRegistration(registration, serviceWorker);

          // Unknown registration or still waiting ⇒ do not guess with a reload
          // (avoids soft loops when SKIP_WAITING never took effect).
          if (!activeRegistration || activeRegistration.waiting) {
            markServiceWorkerUpdatePending();
            settle();
            return;
          }

          // Waiting cleared without a document reload — new SW may already
          // control this client while the document is still the old bundle.
          reloadOnce();
        })();
      }, fallbackReloadMs);
    })();
  });

/** Test-only: reset coalesce + reload guards. */
export const __resetApplyServiceWorkerUpdateForTests = (): void => {
  applyInFlight = null;
  documentReloadScheduled = false;
};

export const markServiceWorkerUpdatePending = (
  storage?: Pick<Storage, 'setItem'>,
): void => {
  try {
    const target = storage ?? window.sessionStorage;
    target.setItem(SW_UPDATE_PENDING_STORAGE_KEY, '1');
  } catch {
    // ignore
  }
};

export const clearServiceWorkerUpdatePending = (
  storage?: Pick<Storage, 'removeItem'>,
): void => {
  try {
    const target = storage ?? window.sessionStorage;
    target.removeItem(SW_UPDATE_PENDING_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const isServiceWorkerUpdatePending = (
  storage?: Pick<Storage, 'getItem'>,
): boolean => {
  try {
    const target = storage ?? window.sessionStorage;
    return target.getItem(SW_UPDATE_PENDING_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};
